import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  TruckIcon, 
  ShieldCheckIcon, 
  ClockIcon,
  StarIcon,
  ArrowRightIcon,
  HomeIcon,
  Squares2X2Icon,
  MagnifyingGlassCircleIcon,
  PuzzlePieceIcon,
  ShoppingCartIcon,
  UserCircleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import SearchBar from '../components/Common/SearchBar';
import DisclaimerModal from '../components/Common/DisclaimerModal';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMakes, selectMakes } from '../store/slices/vehicleSlice';
import { selectCartItemCount } from '../store/slices/cartSlice';
import { selectIsAuthenticated } from '../store/slices/authSlice';
import { getImageUrl } from '../utils/imageUtils';
import Footer from '../components/Layout/Footer';
import useSEO from '../hooks/useSEO';
import { slugify } from '../utils/seo';

const HomePage = () => {
  const navigate = useNavigate();

  // Redirect mobile users to mobile homepage
  const location = useLocation();
  useEffect(() => {
    const checkMobile = () => {
      if (location.pathname === '/' && window.innerWidth < 769) {
        navigate('/mobile', { replace: true });
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [navigate, location.pathname]);

  useSEO({
    title: 'Auto Glass Shop — Windscreens & Auto Glass for All Makes, NSW & VIC',
    description: 'Wholesale auto glass supplier in NSW & VIC. Windscreens, door glass & rear windows for Toyota, Ford, Holden, Mazda, Nissan and 50+ makes. Same-day dispatch.',
    canonical: 'https://www.autoglassshop.com.au/',
  });

  useEffect(() => {
    const schema = [
      {
        '@context': 'https://schema.org',
        '@type': 'AutoPartsStore',
        name: 'Auto Glass Shop',
        url: 'https://www.autoglassshop.com.au',
        logo: 'https://www.autoglassshop.com.au/images/AGS_logo.png',
        image: 'https://www.autoglassshop.com.au/images/AGS_logo.png',
        description: 'Auto Glass Shop is a wholesale automotive glass supplier in Sydney NSW and Melbourne VIC. Supplying windscreens, door glass, rear windows and quarter glass for 50+ vehicle makes. Same-day dispatch, trade pricing.',
        telephone: '+611300002345',
        priceRange: '$$',
        currenciesAccepted: 'AUD',
        paymentAccepted: 'Cash, Credit Card, Bank Transfer',
        areaServed: ['Sydney', 'Melbourne', 'NSW', 'VIC', 'Australia'],
        address: [
          { '@type': 'PostalAddress', streetAddress: 'Unit 9, 8 Cooper Street', addressLocality: 'Smithfield', addressRegion: 'NSW', postalCode: '2164', addressCountry: 'AU' },
          { '@type': 'PostalAddress', streetAddress: '76 Killara Road', addressLocality: 'Campbellfield', addressRegion: 'VIC', postalCode: '3061', addressCountry: 'AU' },
        ],
        openingHoursSpecification: [
          { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '08:00', closes: '16:30' },
          { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Saturday', opens: '08:00', closes: '12:00' },
        ],
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Auto Glass Parts',
          itemListElement: [
            { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Windscreens' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Door Glass' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Rear Windows' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Quarter Glass' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Vent Glass' } },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          { '@type': 'Question', name: 'Do you supply to the public or trade only?', acceptedAnswer: { '@type': 'Answer', text: 'We primarily serve trade customers — glass installers, workshops, smash repairers, and dealerships. Retail customers are welcome to register for an account. Contact us on 1300 002 345.' } },
          { '@type': 'Question', name: 'How do I find the right windscreen for my vehicle?', acceptedAnswer: { '@type': 'Answer', text: 'Use our vehicle finder to select your make, model, and body type, or try the Auto Glass Finder for a guided search. You can also search by VIN for instant vehicle identification.' } },
          { '@type': 'Question', name: 'Do you offer same-day dispatch?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. Orders placed before 2:00 PM for metro Sydney and Melbourne are dispatched the same business day. Regional deliveries typically take 1–3 business days.' } },
          { '@type': 'Question', name: 'What brands of auto glass do you stock?', acceptedAnswer: { '@type': 'Answer', text: 'We stock glass compatible with Toyota, Ford, Holden, Mazda, Nissan, Subaru, Hyundai, Kia, Mitsubishi, Honda, Volkswagen, BMW, Mercedes-Benz and 40+ additional makes.' } },
          { '@type': 'Question', name: 'Can I order accessories with my glass?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. We stock urethane adhesives, primers, moulding clips, suction handles, and cut-out tools. Browse the Accessories & Supplies section.' } },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Popular Vehicle Makes — Auto Glass',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Toyota Auto Glass', url: 'https://www.autoglassshop.com.au/toyota' },
          { '@type': 'ListItem', position: 2, name: 'Ford Auto Glass', url: 'https://www.autoglassshop.com.au/ford' },
          { '@type': 'ListItem', position: 3, name: 'Holden Auto Glass', url: 'https://www.autoglassshop.com.au/holden' },
          { '@type': 'ListItem', position: 4, name: 'Mazda Auto Glass', url: 'https://www.autoglassshop.com.au/mazda' },
          { '@type': 'ListItem', position: 5, name: 'Nissan Auto Glass', url: 'https://www.autoglassshop.com.au/nissan' },
          { '@type': 'ListItem', position: 6, name: 'Hyundai Auto Glass', url: 'https://www.autoglassshop.com.au/hyundai' },
        ],
      },
    ];
    document.querySelectorAll('script[data-page="home"]').forEach(s => s.remove());
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-page', 'home');
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => document.querySelectorAll('script[data-page="home"]').forEach(s => s.remove());
  }, []);

  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const disclaimerKey = 'vehicle_disclaimer_ack';
  const hasAckedDisclaimer = () => {
    try { return sessionStorage.getItem(disclaimerKey) === '1'; } catch { return false; }
  };
  const setAckedDisclaimer = () => {
    try { sessionStorage.setItem(disclaimerKey, '1'); } catch {}
  };
  const proceedToShop = () => {
    if (hasAckedDisclaimer()) {
      navigate('/makes');
    } else {
      setDisclaimerOpen(true);
    }
  };
  const handleFindClick = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    proceedToShop();
  };
  const handleDisclaimerAgree = () => {
    setAckedDisclaimer();
    setDisclaimerOpen(false);
    navigate('/makes');
  };
  const handleDisclaimerClose = () => setDisclaimerOpen(false);
  const features = [
    {
      icon: TruckIcon,
      title: 'Fast Delivery',
      description: 'Same-day delivery available for metro Melbourne. Express shipping nationwide.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Quality Guaranteed',
      description: 'Premium OEM and aftermarket glass with lifetime warranty on workmanship.',
    },
    {
      icon: ClockIcon,
      title: '24/7 Support',
      description: 'Expert technical support and customer service when you need it most.',
    },
  ];

  const dispatch = useDispatch();
  const makes = useSelector(selectMakes);
  const cartCount = useSelector(selectCartItemCount);
  const accessoriesSectionRef = useRef(null);
  const accountSectionRef = useRef(null);
  const [activeFinderTab, setActiveFinderTab] = useState('auto');
  const [activeNav, setActiveNav] = useState('home');

  const heroSectionRef = useRef(null);
  const finderSectionRef = useRef(null);

  useEffect(() => {
    if (!makes || makes.length === 0) {
      dispatch(fetchMakes());
    }
  }, [dispatch, makes]);

  // Add style to ensure logos are displayed without background
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .make-logo {
        background: transparent !important;
        object-fit: contain;
        width: 100%;
        height: 100%;
        max-width: 80px;
        max-height: 40px;
        margin: 0 auto;
        display: block;
      }
      .make-logo-container {
        background: transparent !important;
        min-height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 1rem;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    accountSectionRef.current = null;
  }, []);

  const popularMakes = useMemo(() => {
    const desiredNames = ['Toyota', 'Ford', 'Holden', 'Mazda', 'Nissan', 'Subaru'];
    return desiredNames.map((name) => {
      const match = (makes || []).find((m) => m.name?.toLowerCase() === name.toLowerCase());
      return {
        name,
        logo: match?.logo_url ? getImageUrl('makes', match.logo_url) : null,
        href: match ? `/${slugify(match.name)}` : '/makes',
      };
    });
  }, [makes]);

  const finderTabs = [
    {
      id: 'auto',
      label: 'Auto Glass Finder',
      description: 'Visual journey to narrow positions and features before buying.',
      href: '/parts-finder',
      pill: 'Guided'
    },
    {
      id: 'vin',
      label: 'Search by VIN',
      description: 'Use your 17-digit VIN to pull compatible glass instantly.',
      href: '/autoglass-vin-search',
      pill: 'Fastest'
    }
  ];

  const accessoryHighlights = [
    {
      id: 'kits',
      title: 'Windscreen Fitting Kits',
      description: 'Clips, trims, urethane, and primers bundled per job.',
      tone: 'from Emerald',
      href: '/accessories-supplies'
    },
    {
      id: 'tools',
      title: 'Installer Tools',
      description: 'Cut out sets, suction handles, calibration aids.',
      tone: 'for technicians',
      href: '/accessories-supplies'
    }
  ];

  const testimonials = [
    {
      name: 'Michael Chen',
      role: 'Auto Repair Shop Owner',
      content: 'Excellent quality glass and fast delivery. Auto Glass Shop has been our trusted supplier for over 5 years.',
      rating: 5,
    },
    {
      name: 'Sarah Williams',
      role: 'Fleet Manager',
      content: 'Professional service and competitive pricing. Their customer-specific pricing makes managing our fleet easy.',
      rating: 5,
    },
    {
      name: 'David Thompson',
      role: 'Independent Installer',
      content: 'Great product range and technical support. The vehicle selection system makes finding the right part simple.',
      rating: 5,
    },
  ];

  const mobileNavItems = [
    { id: 'home', label: 'Home', icon: HomeIcon, action: 'scroll', ref: heroSectionRef },
    { id: 'shop', label: 'Shop', icon: Squares2X2Icon, action: 'shop' },
    { id: 'finder', label: 'Finder', icon: MagnifyingGlassCircleIcon, action: 'link', href: '/mobile/parts-finder' },
    { id: 'accessories', label: 'Accessories', icon: PuzzlePieceIcon, action: 'scroll', ref: accessoriesSectionRef },
    { id: 'cart', label: 'Cart', icon: ShoppingCartIcon, action: 'link', href: '/cart', badge: cartCount },
    { id: 'accounts', label: 'Accounts', icon: UserCircleIcon, action: 'link', href: '/mobile/accounts' },
  ];

  const handleBottomNavClick = (item) => {
    setActiveNav(item.id);
    if (item.action === 'scroll' && item.ref?.current) {
      item.ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (item.action === 'link' && item.href) {
      navigate(item.href);
    } else if (item.action === 'shop') {
      proceedToShop();
    }
  };

  const activeFinder = finderTabs.find((tab) => tab.id === activeFinderTab) || finderTabs[0];

  const renderMobileHome = () => (
    <div className="lg:hidden">
      <div className="min-h-screen bg-slate-950 text-white pb-28 overflow-x-hidden">
        <section ref={heroSectionRef} className="px-4 pt-8 pb-8 bg-gradient-to-b from-slate-900 to-slate-950 overflow-hidden">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.35em] text-emerald-200/80">
            <SparklesIcon className="w-4 h-4" />
            Trade Ready
          </div>
          <h1 className="text-[28px] leading-[1.05] font-black text-white mt-3 tracking-tight overflow-hidden">
            <span className="block">Australia Standard Auto Glass,</span>
            <span className="block">ready when you are.</span>
          </h1>
          <p className="text-slate-200 text-[15px] leading-relaxed mt-3">
            Book parts, lock quotes, and source accessories without leaving the bay.
          </p>
          <div className="mt-5">
            <SearchBar
              placeholder="Search SKU, vehicle or keyword"
              className="text-base"
            />
          </div>
          <div className="mt-5 space-y-3">
            <button
              onClick={handleFindClick}
              className="w-full flex items-center justify-between bg-emerald-400 text-slate-900 font-semibold py-3 px-4 rounded-2xl shadow-lg shadow-emerald-500/40"
            >
              Find your vehicle
              <ArrowRightIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate('/parts-finder')}
              className="w-full flex items-center justify-between bg-white/10 border border-white/15 py-3 px-4 rounded-2xl"
            >
              Auto Glass Finder
              <MagnifyingGlassCircleIcon className="w-5 h-5 text-emerald-200" />
            </button>
          </div>
        </section>

        <section ref={finderSectionRef} className="bg-white text-slate-900 rounded-t-3xl -mt-4 pt-8 px-4 pb-10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-slate-400">Finder</p>
              <h2 className="text-xl font-bold">Choose how you want to search</h2>
            </div>
            <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">Live</span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {finderTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveFinderTab(tab.id)}
                className={`rounded-2xl border px-3 py-2 text-left ${
                  activeFinderTab === tab.id
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-200 bg-white text-slate-600'
                }`}
              >
                <div className="text-[11px] uppercase tracking-[0.25em] text-slate-400">{tab.pill}</div>
                <div className="text-base font-semibold">{tab.label}</div>
              </button>
            ))}
          </div>
          <div className="mt-5 bg-slate-900 text-white rounded-3xl p-5 space-y-3">
            <h3 className="text-lg font-semibold">{activeFinder.label}</h3>
            <p className="text-sm text-slate-200">{activeFinder.description}</p>
            <button
              onClick={() => navigate(activeFinder.href)}
              className="w-full flex items-center justify-center gap-2 bg-white text-slate-900 font-semibold py-3 rounded-2xl"
            >
              Launch {activeFinder.label}
              <ArrowRightIcon className="w-4 h-4" />
            </button>
          </div>
        </section>

        <section ref={accessoriesSectionRef} className="bg-white px-4 pb-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Accessories</p>
              <h2 className="text-xl font-bold">Kits that ship with your glass</h2>
            </div>
            <button
              onClick={() => navigate('/accessories-supplies')}
              className="text-sm font-semibold text-emerald-600"
            >
              View all
            </button>
          </div>
          <div className="space-y-3">
            {accessoryHighlights.map((item) => (
              <button
                key={item.id}
                onClick={() => navigate(item.href)}
                className="w-full rounded-3xl border border-slate-200 p-4 text-left bg-gradient-to-r from-slate-50 to-white"
              >
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">{item.tone}</p>
                <h3 className="text-lg font-semibold text-slate-900">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.description}</p>
              </button>
            ))}
          </div>
          <div className="mt-5 rounded-3xl bg-slate-900 text-white p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-300">Items in cart</p>
              <p className="text-2xl font-bold">{cartCount || 0}</p>
            </div>
            <button
              onClick={() => navigate('/cart')}
              className="bg-white text-slate-900 px-4 py-2 rounded-2xl font-semibold"
            >
              Review Cart
            </button>
          </div>
        </section>
      </div>

      <Footer />

      <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-slate-200 bg-white shadow-xl shadow-slate-900/5 lg:hidden">
        <nav className="grid grid-cols-6 text-xs">
          {mobileNavItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleBottomNavClick(item)}
              className={`flex flex-col items-center justify-center gap-1 py-3 ${
                activeNav === item.id ? 'text-slate-900 font-semibold' : 'text-slate-400'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
              {item.badge ? (
                <span className="text-[10px] font-bold text-white bg-emerald-500 rounded-full px-2">
                  {Math.min(item.badge, 99)}
                </span>
              ) : null}
            </button>
          ))}
        </nav>
      </div>
    </div>
  );

  const renderDesktopExperience = () => (
    <div className="hidden lg:block min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-[#f6fff8]">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-4 md:pt-8 md:pb-6">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-[#344e41] leading-tight">
              Australia's Premier Auto Glass Supplier
            </h1>
          </div>
          <div className="mt-2 sm:mt-3 flex justify-center">
            <div className="w-full max-w-xl sm:max-w-2xl">
              <SearchBar
                placeholder="Search by vehicle make, model, or part number..."
                className="text-base sm:text-lg"
              />
            </div>
          </div>
        </div>
        <div className="relative w-full">
          <picture className="block w-full">
            <source srcSet="/hero3.webp" type="image/webp" />
            <img
              src="/hero3.png"
              alt="Auto Glass Shop"
              width="1600"
              height="574"
              fetchpriority="high"
              decoding="async"
              className="w-full h-auto block"
            />
          </picture>
        </div>
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mt-2 sm:mt-3 flex flex-col items-center gap-3 px-4 sm:px-0">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
              <Link
                to="/parts-finder"
                className="btn-primary btn-lg inline-flex items-center w-full sm:w-auto justify-center"
              >
                Auto Glass Finder
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/makes"
                onClick={handleFindClick}
                className="btn-outline btn-lg w-full sm:w-auto text-primary-600 border-2 border-primary-600 hover:bg-primary-600 hover:text-white"
              >
                Find Your Vehicle
              </Link>
              <Link
                to="/autoglass-vin-search"
                className="btn-outline btn-lg w-full sm:w-auto text-primary-600 border-2 border-primary-600 hover:bg-primary-600 hover:text-white"
              >
                Search By VIN
              </Link>
            </div>
            <p className="text-base lg:text-lg text-[#344e41] max-w-3xl text-center px-2">
              Professional-grade automotive glass for workshops, installers, and enthusiasts.
              Find the perfect fit for any vehicle with our advanced vehicle selection system.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Choose Auto Glass Shop?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We're committed to providing the highest quality automotive glass 
              with exceptional service and competitive pricing.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 text-primary-600 rounded-full mb-4">
                  <feature.icon className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Windscreen Guide */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Windscreen Features Guide</h2>
              <p className="text-lg text-gray-700 mb-4">
                The left windscreen image shows the <span className="font-semibold">inside view</span>, and the right windscreen image shows the <span className="font-semibold">outside view</span>.
              </p>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="text-base font-semibold text-gray-900 mb-2">What to look for</h3>
                <ul className="list-disc pl-5 text-gray-700 space-y-1 text-sm">
                  <li><span className="font-medium">Top Shade</span> (green/blue band across the top)</li>
                  <li><span className="font-medium">Sensors</span> such as Rain Sensor “S”, Humidity Sensor “Q”</li>
                  <li><span className="font-medium">Camera</span> “Z”, <span className="font-medium">Driving Recorder</span> “DV”, <span className="font-medium">Antenna</span> “A”</li>
                  <li><span className="font-medium">Mirror Base</span> “M/B” and <span className="font-medium">VIN Notch</span> “V”</li>
                  <li><span className="font-medium">Heater</span> “H”, <span className="font-medium">Moulding</span>, and <span className="font-medium">Ceramic Brand</span> “X”</li>
                </ul>
                <p className="text-xs text-gray-500 mt-3">Tip: Take a clear photo from the driver cabin (inside) and one from the outside. Note any brackets, notches, or sensor windows.</p>
              </div>
            </div>
            <div>
              <figure className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <img
                  src="/windscreen_features.jpg"
                  alt="Windscreen features"
                  width="1400"
                  height="549"
                  className="w-full h-auto object-contain"
                  loading="lazy"
                  decoding="async"
                />
                <figcaption className="text-sm text-gray-600 px-4 py-3 border-t bg-gray-50">
                  Left: Inside view with feature annotations. Right: Outside view. Use both views to match the correct glass.
                </figcaption>
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Makes */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Popular Vehicle Makes
            </h2>
            <p className="text-lg text-gray-600">
              Quick access to glass parts for the most popular vehicle brands
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {popularMakes.map((make, index) => (
              <Link
                key={index}
                to={make.href}
                className="group bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow duration-200 text-center"
              >
                <div className="make-logo-container">
                  <img
                    src={make.logo}
                    alt={`${make.name} logo`}
                    width="40"
                    height="40"
                    loading="lazy"
                    decoding="async"
                    className="make-logo w-10 h-10"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <span 
                    className="text-sm font-semibold text-gray-600 hidden"
                    style={{ display: 'none' }}
                  >
                    {make.name}
                  </span>
                </div>
                <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors duration-200">
                  {make.name}
                </h3>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/makes"
              className="btn-outline inline-flex items-center"
            >
              View All Makes
              <ArrowRightIcon className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Trusted by Professionals
            </h2>
            <p className="text-lg text-gray-600">
              See what our customers say about our products and service
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <blockquote className="text-gray-700 mb-4">
                  "{testimonial.content}"
                </blockquote>
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Content Section — services overview + internal links for crawler discovery */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Australia's Auto Glass Wholesale Supplier</h2>
          <p className="text-lg text-gray-700 mb-6">
            Auto Glass Shop is a leading automotive glass wholesaler supplying windscreens, door glass, rear windows,
            quarter glass and vent glass across <strong>New South Wales</strong> and <strong>Victoria</strong>.
            With warehouses in <strong>Smithfield, Sydney (NSW)</strong> and <strong>Campbellfield, Melbourne (VIC)</strong>,
            we provide same-day dispatch to trade customers, workshops, and glass installers throughout Australia.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-10">
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Our Auto Glass Services</h3>
              <ul className="space-y-3 text-gray-700">
                <li>
                  <strong>Windscreen Replacement Supply:</strong> OEM-matched windscreens for all major makes and models.
                  Browse our <Link to="/makes" className="text-primary-600 hover:underline font-medium">full vehicle catalogue</Link> or
                  use the <Link to="/parts-finder" className="text-primary-600 hover:underline font-medium">Auto Glass Finder</Link> to search by vehicle specification.
                </li>
                <li>
                  <strong>Door Glass &amp; Side Windows:</strong> Tempered door glass panels for front and rear doors.
                  Compatible replacements stocked for 50+ vehicle brands.
                </li>
                <li>
                  <strong>Rear Windows:</strong> Heated and non-heated rear windscreens with matching features including
                  wiper mounts, defrost elements, and antenna connectors.
                </li>
                <li>
                  <strong>Quarter Glass &amp; Vent Glass:</strong> Stationary and sliding quarter glass for vans, wagons and SUVs.
                </li>
                <li>
                  <strong>Installation Accessories:</strong> Urethane adhesives, primers, fitting kits and tools.
                  See our <Link to="/accessories-supplies" className="text-primary-600 hover:underline font-medium">Accessories &amp; Supplies catalogue</Link>.
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Why Workshops Choose Us</h3>
              <ul className="space-y-2 text-gray-700 list-disc list-inside">
                <li>Trade accounts with volume-based pricing tiers</li>
                <li>Same-day dispatch for metro Sydney and Melbourne orders placed before 2 PM</li>
                <li>Over 5,000 SKUs stocked across two Australian warehouses</li>
                <li>Glass that meets Australian Standard AS/NZS 2080</li>
                <li>Online ordering with real-time stock visibility</li>
                <li>Dedicated support team — call <a href="tel:1300002345" className="text-primary-600 hover:underline font-medium">1300 002 345</a></li>
                <li><Link to="/autoglass-vin-search" className="text-primary-600 hover:underline font-medium">VIN search</Link> for instant vehicle identification</li>
              </ul>
            </div>
          </div>

          <div className="mb-10">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Service Coverage</h3>
            <p className="text-gray-700 mb-3">
              We supply auto glass to professional installers, dealerships, smash repairers and fleet managers across:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm text-gray-700">
              {[
                'Sydney CBD &amp; Metro', 'Western Sydney', 'Parramatta &amp; Hills District',
                'Newcastle &amp; Central Coast', 'Wollongong &amp; Illawarra', 'Regional NSW',
                'Melbourne CBD &amp; Metro', 'Northern Suburbs VIC', 'Western Suburbs VIC',
                'Geelong &amp; Surrounds', 'Regional Victoria', 'ACT (via Sydney warehouse)',
              ].map((area) => (
                <div key={area} className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0" />
                  <span dangerouslySetInnerHTML={{ __html: area }} />
                </div>
              ))}
            </div>
          </div>

          {/* FAQ — boosts E-E-A-T and enables FAQ rich results in Google */}
          <div>
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Frequently Asked Questions</h3>
            <div className="space-y-5">
              {[
                {
                  q: 'Do you supply to the public or trade only?',
                  a: 'We primarily serve trade customers — glass installers, workshops, smash repairers, and dealerships. Retail customers are welcome to register for an account. Contact us on 1300 002 345 to discuss your requirements.',
                },
                {
                  q: 'How do I find the right windscreen for my vehicle?',
                  a: 'Use our vehicle finder to select your make, model, and body type, or try the Auto Glass Finder for a guided search. You can also search by VIN for instant vehicle identification.',
                },
                {
                  q: 'Do you offer same-day dispatch?',
                  a: 'Yes. Orders placed before 2:00 PM for metro Sydney and Melbourne are dispatched the same business day. Regional deliveries typically take 1–3 business days via our freight partners.',
                },
                {
                  q: 'What brands of auto glass do you stock?',
                  a: 'We stock glass compatible with Toyota, Ford, Holden, Mazda, Nissan, Subaru, Hyundai, Kia, Mitsubishi, Honda, Volkswagen, BMW, Mercedes-Benz and 40+ additional makes. All glass meets or exceeds Australian standards.',
                },
                {
                  q: 'Can I order accessories with my glass?',
                  a: 'Yes. We stock a full range of installation accessories including urethane adhesives, primers, moulding clips, suction handles, and cut-out tools. Browse the Accessories & Supplies section for the complete range.',
                },
              ].map(({ q, a }) => (
                <div key={q} className="border-l-4 border-primary-200 pl-4">
                  <dt className="font-semibold text-gray-900 mb-1">{q}</dt>
                  <dd className="text-gray-700 text-sm">{a}</dd>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/windscreen-replacement-sydney" className="text-sm text-primary-600 hover:underline font-medium">Windscreen Replacement Sydney →</Link>
            <Link to="/windscreen-replacement-melbourne" className="text-sm text-primary-600 hover:underline font-medium">Windscreen Replacement Melbourne →</Link>
            <Link to="/car-glass-repair-sydney" className="text-sm text-primary-600 hover:underline font-medium">Car Glass Repair Sydney →</Link>
            <Link to="/auto-glass-services-nsw" className="text-sm text-primary-600 hover:underline font-medium">Auto Glass Services NSW →</Link>
            <Link to="/auto-glass-services-vic" className="text-sm text-primary-600 hover:underline font-medium">Auto Glass Services VIC →</Link>
            <Link to="/pages/about-us" className="text-sm text-primary-600 hover:underline font-medium">About Auto Glass Shop →</Link>
            <Link to="/contact" className="text-sm text-primary-600 hover:underline font-medium">Contact &amp; Locations →</Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#3a5a40] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Join thousands of professionals who trust Auto Glass Shop for their automotive glass needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register1"
              className="btn-white btn-lg"
            >
              Create Account
            </Link>
            <Link
              to="/contact"
              className="btn-outline-white btn-lg"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </section>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <DisclaimerModal
        open={disclaimerOpen}
        onClose={handleDisclaimerClose}
        onAgree={handleDisclaimerAgree}
      />
      {renderMobileHome()}
      {renderDesktopExperience()}
    </div>
  );
};

export default HomePage;
